// Contact form — handles challenge loading, PoW computation, and async submission.
// Auth code is derived client-side from a seed (never sent in plaintext)
// and rendered on a <canvas> to resist automated screen-scraping.

import { solveProofOfWork } from './antibot/pow';

interface ChallengeData {
  timing_token: string;
  pow_challenge: string;
  auth_token: string; // seed_hex.hmac_hex — code is derived, never transmitted
}

interface SubmitResponse {
  ok: boolean;
  message: string;
}

// Must match server-side AuthCodeChars
const AUTH_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const CODE_LEN = 5;

// --- Auth code derivation (same algo as server: SHA-256(seed) → indices) ---

async function deriveAuthCode(seed: string): Promise<string> {
  const data = new TextEncoder().encode(seed);
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', data));
  let code = '';
  for (let i = 0; i < CODE_LEN; i++) {
    code += AUTH_CHARS[hash[i] % AUTH_CHARS.length];
  }
  return code;
}

function extractSeed(token: string): string {
  return token.split('.')[0];
}

function randomChar(): string {
  return AUTH_CHARS[Math.floor(Math.random() * AUTH_CHARS.length)];
}

// --- Canvas rendering ---

const CELL_W = 40;
const CELL_H = 48;
const CELL_GAP = 8;
const CANVAS_W = CODE_LEN * CELL_W + (CODE_LEN - 1) * CELL_GAP;
const CANVAS_H = CELL_H;

const COLOR_CYAN = '#00d4ff';
const COLOR_CYAN_DIM = 'rgba(0, 229, 255, 0.25)';
const COLOR_CYAN_GLOW = 'rgba(0, 229, 255, 0.5)';
const COLOR_ORANGE = '#ff6600';
const COLOR_ORANGE_DIM = 'rgba(255, 102, 0, 0.4)';
const COLOR_ORANGE_GLOW = 'rgba(255, 102, 0, 0.6)';
const COLOR_BG_IDLE = 'rgba(0, 229, 255, 0.03)';
const COLOR_BG_REVEALED = 'rgba(10, 15, 25, 0.7)';

function drawAuthChars(
  ctx: CanvasRenderingContext2D,
  chars: string[],
  locked: boolean[],
  revealed: boolean,
  dpr: number
): void {
  ctx.clearRect(0, 0, CANVAS_W * dpr, CANVAS_H * dpr);
  ctx.save();
  ctx.scale(dpr, dpr);

  for (let i = 0; i < CODE_LEN; i++) {
    const x = i * (CELL_W + CELL_GAP);
    const isLocked = locked[i];

    // Cell background
    ctx.fillStyle = revealed ? COLOR_BG_REVEALED : COLOR_BG_IDLE;
    ctx.fillRect(x, 0, CELL_W, CELL_H);

    // Cell border
    ctx.strokeStyle = isLocked ? COLOR_ORANGE_DIM : COLOR_CYAN_DIM;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, 0.5, CELL_W - 1, CELL_H - 1);

    // Text glow
    ctx.shadowBlur = 8;
    if (isLocked) {
      ctx.shadowColor = COLOR_ORANGE_GLOW;
      ctx.fillStyle = COLOR_ORANGE;
    } else {
      ctx.shadowColor = COLOR_CYAN_GLOW;
      ctx.fillStyle = COLOR_CYAN;
    }

    // Text
    ctx.font = '600 20px Rajdhani, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(chars[i], x + CELL_W / 2, CELL_H / 2 + 1);

    ctx.shadowBlur = 0;
  }

  ctx.restore();
}

// --- Main init ---

export function initContactForm(): void {
  const form = document.querySelector<HTMLFormElement>('form[action="/api/contact"]');
  if (!form || form.dataset.initialized) return;
  form.dataset.initialized = '1';

  let challenge: ChallengeData | null = null;

  const authState = createAuthCodeSection(form);
  const feedback = createFeedback(form, authState);
  const overlay = createPowOverlay();

  // Callback: when focus fetches a fresh challenge, update the main one
  authState.challengeUpdate = (fresh: ChallengeData) => {
    challenge = fresh;
  };

  // Load initial challenge
  loadChallenge().then((data) => {
    challenge = data;
    if (challenge) {
      startScramble(authState, challenge.auth_token);
    }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    if (!challenge) {
      showFeedback(feedback, 'error', 'Security challenge not loaded. Please reload the page.');
      return;
    }

    const name = (form.querySelector<HTMLInputElement>('[name="name"]')!).value;
    const email = (form.querySelector<HTMLInputElement>('[name="email"]')!).value;
    const message = (form.querySelector<HTMLTextAreaElement>('[name="message"]')!).value;
    const website = (form.querySelector<HTMLInputElement>('[name="website"]')?.value) ?? '';
    const authInput = authState.input.value;

    if (authInput.trim().length === 0) {
      showFeedback(feedback, 'error', 'Please enter the authorization code.');
      authState.input.focus();
      return;
    }

    showOverlay(overlay);
    let powNonce: string;
    try {
      const result = await solveProofOfWork(challenge.pow_challenge);
      powNonce = result.nonce;
      updateOverlayStatus(overlay, `Verified in ${result.durationMs}ms`);
    } catch {
      hideOverlay(overlay);
      showFeedback(feedback, 'error', 'Security verification failed. Please reload the page.');
      return;
    }

    updateOverlayStatus(overlay, 'Transmitting...');
    try {
      const resp = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          message,
          website,
          timing_token: challenge.timing_token,
          pow_challenge: challenge.pow_challenge,
          pow_nonce: powNonce,
          auth_token: challenge.auth_token,
          auth_input: authInput,
        }),
      });

      const data: SubmitResponse = await resp.json();
      hideOverlay(overlay);

      if (data.ok) {
        showFeedback(feedback, 'success', data.message);
        form.reset();
        stopScramble(authState);
        // Pre-load next challenge so it's ready when user closes feedback
        loadChallenge().then((d) => {
          challenge = d;
          if (challenge) {
            startScramble(authState, challenge.auth_token);
          }
        });
      } else {
        showFeedback(feedback, 'error', data.message);
      }
    } catch {
      hideOverlay(overlay);
      showFeedback(feedback, 'error', 'Network error. Please check your connection and try again.');
    }
  });
}

async function loadChallenge(): Promise<ChallengeData | null> {
  try {
    const resp = await fetch('/api/contact/challenge');
    return await resp.json();
  } catch {
    return null;
  }
}

// --- Auth code scramble / slot machine with canvas ---

interface AuthCodeState {
  container: HTMLDivElement;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  dpr: number;
  input: HTMLInputElement;
  currentChars: string[];
  lockedMask: boolean[];
  realCode: string;
  revealed: boolean;
  scrambleTimer: number | null;
  decelerateTimer: number | null;
  onFocus: (() => void) | null;
  onBlur: (() => void) | null;
  challengeUpdate: (data: ChallengeData) => void;
}

function createAuthCodeSection(form: HTMLFormElement): AuthCodeState {
  const container = document.createElement('div');
  container.className = 'form__group auth-code';

  const label = document.createElement('p');
  label.className = 'auth-code__label';
  label.textContent = 'Authorization code';

  // Canvas for rendering chars (not DOM text → resists scraping)
  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.className = 'auth-code__canvas';
  canvas.width = CANVAS_W * dpr;
  canvas.height = CANVAS_H * dpr;
  canvas.style.width = CANVAS_W + 'px';
  canvas.style.height = CANVAS_H + 'px';
  canvas.style.display = 'block';
  canvas.style.margin = '0 auto var(--space-md, 16px) auto';

  const ctx = canvas.getContext('2d')!;

  const input = document.createElement('input');
  input.className = 'form__input auth-code__input';
  input.type = 'text';
  input.name = 'auth_code';
  input.autocomplete = 'off';
  input.maxLength = CODE_LEN;
  input.placeholder = 'Enter code above';
  input.required = true;
  input.setAttribute('aria-required', 'true');

  container.appendChild(label);
  container.appendChild(canvas);
  container.appendChild(input);

  const submitBtn = form.querySelector('button[type="submit"]');
  form.insertBefore(container, submitBtn);

  const currentChars = Array.from({ length: CODE_LEN }, () => randomChar());
  const lockedMask = Array(CODE_LEN).fill(false) as boolean[];

  // Initial draw
  drawAuthChars(ctx, currentChars, lockedMask, false, dpr);

  return {
    container,
    canvas,
    ctx,
    dpr,
    input,
    currentChars,
    lockedMask,
    realCode: '',
    revealed: false,
    scrambleTimer: null,
    decelerateTimer: null,
    onFocus: null,
    onBlur: null,
    challengeUpdate: () => {},
  };
}

function startScramble(state: AuthCodeState, authToken: string): void {
  stopScramble(state);

  // Reset visual state
  for (let i = 0; i < CODE_LEN; i++) {
    state.currentChars[i] = randomChar();
    state.lockedMask[i] = false;
  }
  state.revealed = false;
  drawAuthChars(state.ctx, state.currentChars, state.lockedMask, false, state.dpr);

  // Fast scramble while not focused
  state.scrambleTimer = window.setInterval(() => {
    for (let i = 0; i < CODE_LEN; i++) {
      state.currentChars[i] = randomChar();
    }
    drawAuthChars(state.ctx, state.currentChars, state.lockedMask, false, state.dpr);
  }, 80);

  state.realCode = ''; // Will be derived on focus

  // Focus → fetch new challenge, derive code, decelerate and reveal
  state.onFocus = async () => {
    if (state.revealed) return;
    // Fetch a fresh challenge each time
    const fresh = await loadChallenge();
    if (fresh) {
      state.challengeUpdate(fresh);
      const seed = extractSeed(fresh.auth_token);
      state.realCode = await deriveAuthCode(seed);
    }
    state.revealed = false; // Will be set true at end of deceleration
    decelerateAndReveal(state);
  };

  // Blur → restart scramble
  state.onBlur = () => {
    if (!state.revealed) return;
    state.revealed = false;
    for (let i = 0; i < CODE_LEN; i++) {
      state.lockedMask[i] = false;
    }
    state.scrambleTimer = window.setInterval(() => {
      for (let i = 0; i < CODE_LEN; i++) {
        state.currentChars[i] = randomChar();
      }
      drawAuthChars(state.ctx, state.currentChars, state.lockedMask, false, state.dpr);
    }, 80);
  };

  state.input.addEventListener('focus', state.onFocus);
  state.input.addEventListener('blur', state.onBlur);
}

function decelerateAndReveal(state: AuthCodeState): void {
  // Stop fast scramble
  if (state.scrambleTimer !== null) {
    clearInterval(state.scrambleTimer);
    state.scrambleTimer = null;
  }

  const totalDuration = 750 + Math.random() * 500; // 750-1250ms
  const startTime = performance.now();
  const code = state.realCode;
  const startInterval = 60;
  const endInterval = 350;

  function tick(): void {
    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / totalDuration, 1);
    // Ease-out quadratic
    const eased = 1 - (1 - progress) * (1 - progress);

    // Progressively lock chars from left to right
    for (let i = 0; i < CODE_LEN; i++) {
      const threshold = (i + 1) / CODE_LEN;
      if (eased >= threshold && code[i]) {
        state.currentChars[i] = code[i];
        state.lockedMask[i] = true;
      } else {
        state.currentChars[i] = randomChar();
      }
    }

    drawAuthChars(state.ctx, state.currentChars, state.lockedMask, true, state.dpr);

    if (progress >= 1) {
      // Final: all locked
      for (let i = 0; i < CODE_LEN; i++) {
        if (code[i]) {
          state.currentChars[i] = code[i];
          state.lockedMask[i] = true;
        }
      }
      drawAuthChars(state.ctx, state.currentChars, state.lockedMask, true, state.dpr);
      state.revealed = true;
      return;
    }

    const nextDelay = startInterval + (endInterval - startInterval) * eased;
    state.decelerateTimer = window.setTimeout(tick, nextDelay);
  }

  tick();
}

function stopScramble(state: AuthCodeState): void {
  if (state.scrambleTimer !== null) {
    clearInterval(state.scrambleTimer);
    state.scrambleTimer = null;
  }
  if (state.decelerateTimer !== null) {
    clearTimeout(state.decelerateTimer);
    state.decelerateTimer = null;
  }
  if (state.onFocus) {
    state.input.removeEventListener('focus', state.onFocus);
    state.onFocus = null;
  }
  if (state.onBlur) {
    state.input.removeEventListener('blur', state.onBlur);
    state.onBlur = null;
  }
}

// --- UI helpers ---

interface FeedbackState {
  el: HTMLDivElement;
  messageEl: HTMLSpanElement;
  authState: AuthCodeState;
}

function createFeedback(form: HTMLFormElement, authState: AuthCodeState): FeedbackState {
  const div = document.createElement('div');
  div.className = 'form__feedback';
  div.setAttribute('role', 'alert');

  const messageEl = document.createElement('span');

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'form__feedback__close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.innerHTML = '&#x2715;'; // ✕
  closeBtn.addEventListener('click', () => {
    div.className = 'form__feedback';
    authState.container.style.display = '';
  });

  div.appendChild(messageEl);
  div.appendChild(closeBtn);

  // Insert in the same position as the auth code section
  form.insertBefore(div, authState.container);

  return { el: div, messageEl, authState };
}

function showFeedback(fb: FeedbackState, type: 'success' | 'error', message: string): void {
  fb.el.className = `form__feedback form__feedback--visible form__feedback--${type}`;
  fb.messageEl.textContent = message;
  // Hide auth code section, show feedback in its place
  fb.authState.container.style.display = 'none';
  fb.el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function createPowOverlay(): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.className = 'pow-overlay';
  overlay.setAttribute('aria-live', 'polite');
  overlay.innerHTML =
    '<div class="pow-overlay__terminal">' +
    '<div class="pow-overlay__title">Transmission in progress...</div>' +
    '<div class="pow-overlay__progress"><div class="pow-overlay__progress-bar"></div></div>' +
    '<div class="pow-overlay__status">Verifying identity...</div>' +
    '</div>';
  document.body.appendChild(overlay);
  return overlay;
}

function showOverlay(overlay: HTMLDivElement): void {
  overlay.classList.add('pow-overlay--active');
}

function hideOverlay(overlay: HTMLDivElement): void {
  overlay.classList.remove('pow-overlay--active');
}

function updateOverlayStatus(overlay: HTMLDivElement, text: string): void {
  const status = overlay.querySelector('.pow-overlay__status');
  if (status) status.textContent = text;
}
